import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FramedPhoto from "@/components/FramedPhoto";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import {
  SectionLabel,
  PrimaryButton,
  BulletList,
  CTASection,
} from "@/components/Section";
import {
  Crown,
  Settings,
  Calculator,
  TrendingUp,
  Megaphone,
  ClipboardList,
  Receipt,
  HardHat,
  Users,
  BrainCircuit,
  Code2,
  Handshake,
  Shuffle,
  Target,
  Boxes,
  TrendingDown,
  RefreshCw,
  Clock,
  UserX,
  ClipboardCheck,
  User,
  Workflow,
  ShieldCheck,
  Calendar,
  CheckCircle2,
  PieChart,
  Tag,
  FileBarChart,
  LayoutGrid,
} from "lucide-react";

const services = [
  { icon: Crown, title: "Fractional CEO", desc: "Executive planning & strategic direction" },
  { icon: Settings, title: "Fractional COO", desc: "Operational rhythm, systems & reporting" },
  { icon: Calculator, title: "Fractional CFO", desc: "Financial structure, forecasting & cashflow" },
  { icon: Handshake, title: "Commercial Support", desc: "Pricing, contracts, claims & margin discipline" },
  { icon: TrendingUp, title: "Fractional Sales", desc: "Growth plans, sales processes & fractional sales teams" },
];

const problems = [
  "The owner ends up doing everything.",
  "Systems get messy.",
  "Reporting falls behind.",
  "Growth becomes reactive.",
  "Hiring full-time senior roles is expensive — but doing nothing costs more.",
];

const results = [
  "Clear reporting",
  "Predictable systems",
  "Less firefighting",
  "Better margins",
  "Scalable operations",
];

const truths = [
  {
    title1: "Chaos costs money.",
    title2: "Structure makes profit.",
    sub: "Most businesses don't lose money from lack of work — they lose it through poor systems.",
    bgImage:
      "https://customer-assets.emergentagent.com/job_ops-framework/artifacts/q248eviz_Messenger_creation_557A4167-F20A-4B0A-8222-3EF924F2F9A5.jpeg",
    icons: [
      { Icon: TrendingDown, label: "Missed margins" },
      { Icon: RefreshCw, label: "Rework" },
      { Icon: Clock, label: "Slow invoicing" },
      { Icon: UserX, label: "Confused teams" },
    ],
    bar: "Fix the structure. Grow the business.",
  },
  {
    title1: "Stop hiring into chaos.",
    title2: "Fix the system first.",
    sub: "More people won't fix broken workflows.",
    bgImage:
      "https://customer-assets.emergentagent.com/job_ops-framework/artifacts/3by6kwna_Messenger_creation_4CFFE853-6FBF-4279-A02F-5336857AB035.jpeg",
    icons: [
      { Icon: ClipboardCheck, label: "Clear processes" },
      { Icon: User, label: "Defined roles" },
      { Icon: Workflow, label: "Repeatable workflow" },
      { Icon: ShieldCheck, label: "Accountability rhythm" },
    ],
    bar: "Structure first. Then scale.",
  },
  {
    title1: "If reporting is late —",
    title2: "decisions are wrong.",
    sub: "Clarity beats guesswork every time.",
    bgImage:
      "https://customer-assets.emergentagent.com/job_ops-framework/artifacts/54uldudg_Messenger_creation_AA118944-B641-49C9-8E7B-F4A0AC542911.jpeg",
    icons: [
      { Icon: Calendar, label: "Weekly rhythm" },
      { Icon: PieChart, label: "Margin visibility" },
      { Icon: Tag, label: "Cost tracking" },
      { Icon: TrendingUp, label: "Forecast confidence" },
    ],
    bar: "Numbers drive discipline.",
  },
  {
    title1: "Growth exposes",
    title2: "weak systems.",
    bgImage:
      "https://customer-assets.emergentagent.com/job_ops-framework/artifacts/ewf5a3io_Messenger_creation_F2758CFA-025E-4A80-82BF-8013FE90AE4E.jpeg",
    tiers: [
      { revenue: "$2M", state: "Manageable" },
      { revenue: "$5M", state: "Stressful" },
      { revenue: "$10M+", state: "Dangerous" },
    ],
    icons: [
      { Icon: Settings, label: "Systems" },
      { Icon: FileBarChart, label: "Reporting" },
      { Icon: LayoutGrid, label: "Structure" },
      { Icon: User, label: "Leadership rhythm" },
    ],
    bar: "Build for scale early.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-[#E2E8F0] overflow-hidden">
        <div className="bb-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-10 lg:pt-16 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">            <SectionLabel>Fractional Operations · NZ</SectionLabel>
            <h1
              className="bb-fade-up mt-5 text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-[#0A192F]"
              data-testid="home-hero-title"
            >
              You run the business.
              <br />
              <span className="text-[#F68712]">We build the structure.</span>
            </h1>
            <p
              className="bb-fade-up bb-delay-2 mt-8 text-lg lg:text-xl text-[#475569] leading-relaxed max-w-2xl"
              data-testid="home-hero-subtitle"
            >
              Systems, reporting and workflow structure built for trade and service businesses.
            </p>
            <div className="bb-fade-up bb-delay-3 mt-10 flex flex-wrap items-center gap-4">
              <PrimaryButton to="/contact" testId="home-hero-cta-book-call">
                Book a Free Call
              </PrimaryButton>
            </div>
            <p
              className="bb-fade-up bb-delay-4 mt-6 text-sm font-mono tracking-wide text-[#475569]"
              data-testid="home-hero-revenue-line"
            >
              Built for businesses doing $2M–$15M+ revenue.
            </p>
          </div>

          <div className="bb-fade-up bb-delay-3 lg:col-span-5">
            <FramedPhoto
              src="https://customer-assets.emergentagent.com/job_ops-framework/artifacts/e6khyz5c_Messenger_creation_5C676094-F1BE-47C4-B5A6-1BD26C0AC17C.jpeg"
              alt="Site engineer reviewing structural plans on construction site"
              aspect="aspect-[4/5]"
              eyebrow="OPERATIONS · ON-SITE"
              label="STRUCTURE & SYSTEMS"
              testId="home-hero-photo"
            />
          </div>
        </div>
      </section>

      {/* MARQUEE — brand rhythm strip */}
      <Marquee
        testId="home-marquee"
        speed={55}
        items={[
          "CHAOS COSTS MONEY",
          "STRUCTURE MAKES PROFIT",
          "YOUR TEAM · AT FRACTIONAL CAPACITY",
          "SYSTEMS · REPORTING · RHYTHM",
          "BUILT FOR $2M–$15M+ BUSINESSES",
          "OPERATIONAL INFRASTRUCTURE FOR SMEs",
        ]}
      />

      {/* WHY BACKBENCH */}
      <section
        className="border-b border-[#E2E8F0] bg-[#F8FAFC]"
        data-testid="home-why-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel color="navy">01 — Why Backbench</SectionLabel>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
                Most operational businesses don't struggle because there's no work.
                <br />
                <span className="text-[#F68712]">They struggle because growth outpaces structure.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:pl-10 lg:border-l border-[#0A192F]/15">
            <Reveal delay={120}>
              <ul className="space-y-3 font-mono text-sm lg:text-base text-[#0A192F]" data-testid="home-growth-list">
                {["More customers.", "More staff.", "More vehicles.", "More projects."].map((line) => (
                  <li key={line} className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-[#F68712] flex-shrink-0" />
                    <span className="font-semibold">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 space-y-4 text-base lg:text-lg text-[#475569] leading-relaxed">
                <p>Eventually the systems that got the business to $2M stop working at $5M.</p>
                <p className="text-[#0A192F] font-semibold">Margins tighten. Reporting falls behind. Decisions become reactive. The owner becomes the bottleneck.</p>
                <p className="pt-4 border-t border-[#0A192F]/10">
                  Backbench exists to build the operational backbone that allows businesses to keep growing — without relying on the owner to hold everything together.
                </p>
                <p className="text-[#0A192F] font-bold text-lg">
                  Not theory. <span className="text-[#F68712]">Structure.</span>
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            <Reveal delay={240} direction="left">
              <FramedPhoto
                src="https://customer-assets.emergentagent.com/job_ops-framework/artifacts/yk7s92hw_Messenger_creation_94542925-D09C-4B03-93C2-2E9F8787C57F.jpeg"
                alt="Owner-operator working under pressure"
                aspect="aspect-[4/5]"
                eyebrow="REALITY"
                label="OWNER AS BOTTLENECK"
                testId="home-why-photo"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE VISION — Monday Morning Clarity */}
      <section
        className="border-b border-[#E2E8F0] bg-white"
        data-testid="home-vision-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>02 — The Vision</SectionLabel>
                <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] text-[#0A192F]">
                  The business you've been trying to build.
                </h2>
                <p className="mt-8 text-lg lg:text-xl text-[#475569] leading-relaxed max-w-md">
                  Imagine walking into Monday already knowing:
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-7 lg:pl-12 lg:border-l border-[#0A192F]/15">
              <Reveal delay={120}>
                <ul className="space-y-4 lg:space-y-5" data-testid="home-vision-list">
                  {[
                    "What last week made.",
                    "What cash looks like.",
                    "Which jobs are profitable.",
                    "Which managers need support.",
                    "What the priorities are this week.",
                  ].map((line, i) => (
                    <li key={line} className="flex items-start gap-4">
                      <span className="mt-2 w-2 h-2 bg-[#F68712] flex-shrink-0" />
                      <span className="text-xl lg:text-2xl font-bold text-[#0A192F] leading-snug">
                        {line}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-12 pt-8 border-t border-[#0A192F]/10 space-y-2">
                  <p className="font-mono text-sm lg:text-base text-[#475569] tracking-wide">
                    No surprises.
                  </p>
                  <p className="font-mono text-sm lg:text-base text-[#475569] tracking-wide">
                    No chasing information.
                  </p>
                  <p className="font-mono text-sm lg:text-base text-[#475569] tracking-wide">
                    No running the business from your phone.
                  </p>
                </div>

                <p className="mt-10 text-2xl lg:text-3xl font-extrabold tracking-tight text-[#0A192F]">
                  Just clarity.
                </p>
                <p className="mt-3 text-lg lg:text-xl text-[#0A192F] font-semibold">
                  That's what <span className="text-[#F68712]">structure</span> gives you.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* OUR MODEL */}
      <section
        className="border-b border-[#E2E8F0] bg-[#0A192F] text-white"
        data-testid="home-model-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F68712]">
              Our Model
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
              Your team.
              <br />
              At fractional capacity.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/15 space-y-6">
            <p className="text-lg lg:text-xl font-medium leading-relaxed">
              Backbench isn't a consultancy. We're a fractional team that becomes part of yours.
            </p>
            <p className="text-lg text-white/75 leading-relaxed">
              You get access to senior operators, AI specialists, software architects and
              governance talent — the kind of people that would normally be out of reach for
              a $2M–$15M+ business.
            </p>
            <p className="text-lg lg:text-xl text-white font-semibold leading-relaxed">
              Embedded at the capacity you need. Always part of your team — never external advisors.
            </p>
            <div className="pt-4 grid grid-cols-1 sm:grid-cols-3 gap-0 border-t border-l border-white/15 mt-4">
              {[
                { k: "Senior", v: "Operator-led" },
                { k: "Fractional", v: "Hours, not headcount" },
                { k: "Embedded", v: "Part of your team" },
              ].map((item) => (
                <div
                  key={item.k}
                  className="border-r border-b border-white/15 p-5 lg:p-6"
                >
                  <div className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                    {item.k.toUpperCase()}
                  </div>
                  <div className="mt-2 text-base lg:text-lg font-bold">
                    {item.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUTHS / BRAND VOICE */}
      <section
        className="border-b border-[#E2E8F0] bg-white"
        data-testid="home-truths-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionLabel>What we've seen</SectionLabel>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <h2 className="lg:col-span-7 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
              Four truths from inside the businesses we work with.
            </h2>
            <p className="lg:col-span-5 text-base lg:text-lg text-[#475569] leading-relaxed">
              The same patterns show up in every owner-led business that hits a ceiling. These are the ones we fix first.
            </p>
          </div>

          <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {truths.map((t, i) => (
              <Reveal key={i} delay={i * 120} direction="up">
              <article
                key={i}
                className="relative bg-[#0A192F] text-white p-8 lg:p-12 flex flex-col min-h-[520px] overflow-hidden"
                data-testid={`home-truth-${i}`}
              >
                {/* Background image (optional) */}
                {t.bgImage && (
                  <>
                    <img
                      src={t.bgImage}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover object-[70%_center] sm:object-right pointer-events-none select-none"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/30 pointer-events-none" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/85 via-transparent to-transparent pointer-events-none" />
                  </>
                )}

                {/* Top meta */}
                <div className="relative flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                    {String(i + 1).padStart(2, "0")} · BACKBENCH
                  </span>
                  <span className="w-2 h-2 bg-[#F68712]" />
                </div>

                {/* Headline */}
                <div className="relative mt-10 lg:mt-14 flex-1 flex flex-col justify-start">
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.02]">
                    <span className="block text-white">{t.title1}</span>
                    <span className="block text-[#F68712]">{t.title2}</span>
                  </h3>

                  {t.sub && (
                    <p className="mt-6 text-base lg:text-lg text-white/80 leading-relaxed max-w-md">
                      {t.sub}
                    </p>
                  )}

                  {t.tiers && (
                    <div className="mt-6 space-y-2 max-w-sm">
                      {t.tiers.map((tier) => (
                        <div
                          key={tier.revenue}
                          className="flex items-center gap-4"
                        >
                          <span className="bg-[#F68712] text-[#0A192F] font-extrabold tracking-wide px-3 py-1.5 text-sm w-20 text-center">
                            {tier.revenue}
                          </span>
                          <span className="text-base lg:text-lg font-semibold text-white">
                            {tier.state}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Icons row */}
                <div className="relative mt-10 pt-6 border-t border-white/15 grid grid-cols-4 gap-3">
                  {t.icons.map((ic, idx) => {
                    const I = ic.Icon;
                    return (
                      <div
                        key={idx}
                        className="flex flex-col items-start gap-2.5"
                      >
                        <I size={22} strokeWidth={1.75} className="text-[#F68712]" />
                        <span className="text-[11px] text-white/80 font-medium leading-tight">
                          {ic.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Green CTA bar */}
                <div className="relative mt-6 -mx-8 lg:-mx-12 -mb-8 lg:-mb-12 bg-[#F68712] text-[#0A192F] px-8 lg:px-12 py-4 flex items-center justify-between">
                  <span className="text-sm font-bold tracking-wide">
                    {t.bar}
                  </span>
                  <ArrowRight size={16} strokeWidth={2.5} />
                </div>

                {/* Corner ticks */}
                <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#F68712] z-10" />
                <span className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#F68712] z-10" />
              </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section
        className="border-b border-[#E2E8F0] bg-white"
        data-testid="home-services-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-20 lg:pt-32 pb-0">
          <SectionLabel>03 — What We Do</SectionLabel>
          <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F] max-w-3xl">
              We plug in the right experts.
            </h2>
            <Link
              to="/what-we-do"
              className="text-sm font-bold tracking-widest uppercase text-[#0A192F] hover:text-[#F68712] inline-flex items-center gap-2 transition-colors"
              data-testid="home-services-see-all-link"
            >
              See full breakdown
              <ArrowRight size={16} strokeWidth={2.25} />
            </Link>
          </div>
          <p className="mt-6 text-base lg:text-lg text-[#475569] leading-relaxed max-w-3xl">
            Five senior seats — CEO, COO, CFO, Commercial and Sales — provided directly.
            For everything else (H&amp;S, HR, marketing, QS, accounting), we bring in
            other expert services under one operating framework.
          </p>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 lg:pb-32 mt-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E2E8F0]">
            {services.map((s, idx) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={(idx % 4) * 60}>
                  <div
                    className="bb-card p-7 lg:p-8 border-r border-b border-[#E2E8F0] flex flex-col gap-5 min-h-[200px] h-full"
                    data-testid={`home-service-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon size={22} strokeWidth={1.5} className="text-[#0A192F]" />
                      <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="mt-auto">
                      <h3 className="text-lg lg:text-xl font-extrabold tracking-tight text-[#0A192F] leading-snug">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}

            {/* Card 15 — Custom Mix info */}
            <Reveal delay={120}>
              <div
                className="bb-card bg-[#F8FAFC] p-7 lg:p-8 border-r border-b border-[#E2E8F0] flex flex-col gap-5 min-h-[200px] h-full"
                data-testid="home-service-custom"
              >
                <div className="flex items-center justify-between">
                  <Boxes size={22} strokeWidth={1.5} className="text-[#0A192F]" />
                  <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                    CUSTOM
                  </span>
                </div>
                <div className="mt-auto">
                  <h3 className="text-lg lg:text-xl font-extrabold tracking-tight text-[#0A192F] leading-snug">
                    Your custom mix.
                  </h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                    Pick the seats. Scale the capacity. We tailor the team to you.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Card 16 — CTA */}
            <Reveal delay={180}>
              <Link
                to="/contact"
                className="group bg-[#0A192F] text-white p-7 lg:p-8 border-r border-b border-[#0A192F] flex flex-col gap-5 min-h-[200px] h-full hover:bg-[#F68712] transition-colors duration-300"
                data-testid="home-service-cta"
              >
                <div className="flex items-center justify-between">
                  <ArrowRight
                    size={22}
                    strokeWidth={1.75}
                    className="text-[#F68712] group-hover:text-white transition-colors duration-300"
                  />
                  <span className="font-mono text-[11px] tracking-widest text-[#F68712] group-hover:text-white font-bold transition-colors duration-300">
                    NEXT STEP
                  </span>
                </div>
                <div className="mt-auto">
                  <h3 className="text-lg lg:text-xl font-extrabold tracking-tight text-white leading-snug">
                    Start the conversation.
                  </h3>
                  <p className="mt-2 text-sm text-white/70 group-hover:text-white transition-colors duration-300">
                    Book a free call — we'll map your current structure in 30 minutes.
                  </p>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section
        className="border-b border-[#E2E8F0] bg-[#F8FAFC]"
        data-testid="home-results-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel color="navy">04 — Results</SectionLabel>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
                What changes when Backbench is in place.
              </h2>
            </Reveal>

            {/* Animated stats strip */}
            <div className="mt-10 grid grid-cols-3 gap-0 border-t border-l border-[#0A192F]/10">
              <div className="bb-card bg-white border-r border-b border-[#0A192F]/10 p-5 lg:p-6">
                <div className="font-mono text-[10px] tracking-widest text-[#F68712] font-bold">
                  REVENUE FIT
                </div>
                <div className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0A192F]">
                  <CountUp end={2} prefix="$" suffix="M+" />
                </div>
                <div className="mt-1 text-[11px] text-[#475569]">minimum revenue</div>
              </div>
              <div className="bb-card bg-white border-r border-b border-[#0A192F]/10 p-5 lg:p-6">
                <div className="font-mono text-[10px] tracking-widest text-[#F68712] font-bold">
                  CORE SEATS
                </div>
                <div className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0A192F]">
                  <CountUp end={5} />
                </div>
                <div className="mt-1 text-[11px] text-[#475569]">senior fractional roles</div>
              </div>
              <div className="bb-card bg-white border-r border-b border-[#0A192F]/10 p-5 lg:p-6">
                <div className="font-mono text-[10px] tracking-widest text-[#F68712] font-bold">
                  HOURS NOT HIRES
                </div>
                <div className="mt-3 text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0A192F]">
                  <CountUp end={0} suffix="%" />
                </div>
                <div className="mt-1 text-[11px] text-[#475569]">full-time overhead</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-[#0A192F]/15">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-[#0A192F]/10">
              {results.map((r, i) => (
                <Reveal key={r} delay={i * 80}>
                  <div
                    className="bb-card border-r border-b border-[#0A192F]/10 bg-white p-6 lg:p-8 flex items-center gap-4 h-full"
                    data-testid={`home-result-${i}`}
                  >
                    <span className="font-mono text-[11px] tracking-widest text-[#F68712]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-lg lg:text-xl font-bold text-[#0A192F]">
                      {r}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SIX MONTHS FROM NOW — Transformation */}
      <section
        className="border-b border-[#E2E8F0] bg-[#0A192F] text-white"
        data-testid="home-six-months-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12 lg:mb-16">
            <div className="lg:col-span-7">
              <SectionLabel>Six months from now</SectionLabel>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02]">
                What success looks like.
              </h2>
            </div>
            <p className="lg:col-span-5 text-base lg:text-lg text-white/70 leading-relaxed">
              Six months after Backbench is embedded, your business should feel different. Here's what that looks like.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/15">
            {[
              "You know exactly how the business is performing.",
              "Managers own their departments.",
              "Financial reporting is timely and reliable.",
              "Cashflow is planned, not guessed.",
              "Meetings produce decisions and accountability.",
              "Systems replace constant firefighting.",
              "Teams understand expectations.",
              "The owner spends more time leading, not solving every problem.",
            ].map((outcome, i) => (
              <Reveal key={i} delay={(i % 4) * 60}>
                <div
                  className="border-r border-b border-white/15 p-6 lg:p-7 min-h-[180px] h-full flex flex-col gap-4"
                  data-testid={`home-six-months-outcome-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <CheckCircle2 size={22} strokeWidth={1.75} className="text-[#F68712]" />
                    <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="mt-auto text-base lg:text-lg font-semibold leading-snug text-white">
                    {outcome}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-12 lg:mt-16 text-xl lg:text-2xl font-bold text-white/90 max-w-2xl">
            That's the outcome we're working towards.
          </p>
        </div>
      </section>

      <CTASection />
    </div>
  );
}

